import {
  castErrorResult,
  createErrorResult,
  createSuccessResult,
  ERRORS,
  IFiscalClient,
  IReceipt,
  IReceiptWithSource,
  SimpleResult,
} from "@prostocheck/shared";

import { requestCheck, requestShiftDocuments } from "../commands";
import { mapWithConcurrency } from "../concurrency";
import { parseReceipt } from "../receipt-xml";
import { IReadPeriodParams, readShifts } from "../shifts";
import { IReceiptSummary } from "./receipts.models";
import { isTransactionDocument, toReceiptSummary } from "./receipts.utils";

/**
 * One receipt, parsed from its original XML. `cabinetUrl` is what ДПС itself
 * links to; prefer it over building the URL ourselves.
 */
export const readReceipt = async ({
  client,
  registrarFiscalNumber,
  fiscalNumber,
}: {
  client: IFiscalClient;
  registrarFiscalNumber: string;
  fiscalNumber: string;
}): Promise<SimpleResult<IReceiptWithSource>> => {
  const result = await requestCheck({
    client,
    registrarFiscalNumber,
    documentFiscalNumber: fiscalNumber,
  });

  if (!result.success) {
    return castErrorResult(result);
  }

  if (!result.data?.Data) {
    return createErrorResult(ERRORS.fiscal.documentNotFound, fiscalNumber);
  }

  return createSuccessResult({
    receipt: parseReceipt({ data: result.data.Data, fiscalNumber }),
    cabinetUrl: result.data.CabinetUrl,
  });
};

/**
 * Receipt headers over a period, newest first. Cheap: one `Shifts` call plus
 * one `Documents` call per shift, no document bodies.
 */
export const readReceiptSummaries = async ({
  client,
  registrarFiscalNumber,
  from,
  to,
}: IReadPeriodParams): Promise<SimpleResult<IReceiptSummary[]>> => {
  const shiftsResult = await readShifts({
    client,
    registrarFiscalNumber,
    from,
    to,
  });

  if (!shiftsResult.success) {
    return castErrorResult(shiftsResult);
  }

  const perShift = await mapWithConcurrency({
    items: shiftsResult.data ?? [],
    map: async shift => {
      const documents = await requestShiftDocuments({
        client,
        registrarFiscalNumber,
        shiftId: shift.ShiftId,
      });

      return (documents.data?.Documents ?? [])
        .filter(isTransactionDocument)
        .map(toReceiptSummary);
    },
  });

  return createSuccessResult(
    perShift.flat().sort((a, b) => b.issuedAt.localeCompare(a.issuedAt)),
  );
};

/**
 * Full receipts over a period. Expensive by construction: ai/docs/prro-api.md
 * has no bulk read, so every total and payment method costs one `CheckExt`
 * call. Keep the range short — the receipts screen defaults to a single day.
 *
 * A receipt that cannot be fetched is dropped rather than failing the list; the
 * usual reason is an offline document ДПС has not received yet.
 */
export const readReceipts = async (
  params: IReadPeriodParams,
): Promise<SimpleResult<IReceipt[]>> => {
  const summaries = await readReceiptSummaries(params);

  if (!summaries.success) {
    return castErrorResult(summaries);
  }

  const results = await mapWithConcurrency({
    items: summaries.data ?? [],
    map: summary =>
      readReceipt({
        client: params.client,
        registrarFiscalNumber: params.registrarFiscalNumber,
        fiscalNumber: summary.fiscalNumber,
      }),
  });

  return createSuccessResult(
    results
      .filter(result => result.success && result.data)
      .map(result => result.data!.receipt),
  );
};
